import { c as _c } from "react/compiler-runtime";
import { Box, ButtonBase, Collapse, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useMemo, useState } from 'react';
import { ColorBox } from '@/components/display/ColorBox/ColorBox';
import { ButtonGroup } from '@/components/form/baseInputs/ButtonGroup';
import { ButtonGroupInputOption } from '@/components/form/baseInputs/inputTypes';
import { AlertTriangleIcon } from '@/components/icons/AlertTriangleIcon';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { COLORS } from '@/styles/tokens/colors';
import { AnswerReviewFieldRow } from './AnswerReviewFieldRow';
import { AnswerReviewFilter, AnswerReviewViewModel, AnswerReviewWorkbenchProps } from './AnswerReviewWorkbench.types';
import { fieldMatchesFilter, filterAnswerReviewSections, getActiveFields, getAnswerReviewSummary } from './AnswerReviewWorkbench.utils';
import { useAnswerReviewInteractions } from './hooks/useAnswerReviewInteractions';
import { RecursiveAnswerEditor } from './RecursiveAnswerEditor';
type ReviewCallbacks = Pick<AnswerReviewWorkbenchProps, 'onAnswerChange' | 'onRecursiveChange' | 'onAnswerBlur' | 'onSelectEvidence' | 'selectedEvidence' | 'onFocusField' | 'onCloseField' | 'onCancelCitation' | 'onAddCitation' | 'focusedFieldId' | 'citationAction' | 'isDonePending'>;
interface AnswerReviewPanelProps extends ReviewCallbacks {
  review: AnswerReviewViewModel;
}
const FILTER_LABELS: Record<AnswerReviewFilter, string> = {
  all: 'All',
  high: 'High confidence',
  needsReview: 'Needs review',
  edited: 'Edited'
};
const FILTER_COLORS: Partial<Record<AnswerReviewFilter, string>> = {
  high: COLORS.TEAL[300],
  needsReview: COLORS.FUNCTIONAL.WARNING[500],
  edited: COLORS.GRAY[600]
};
export function AnswerReviewPanel(t0) {
  const $ = _c(105);
  const {
    review,
    onAnswerChange,
    onRecursiveChange,
    onAnswerBlur,
    onSelectEvidence,
    selectedEvidence,
    onFocusField,
    onCloseField,
    onCancelCitation,
    onAddCitation,
    focusedFieldId,
    citationAction,
    isDonePending
  } = t0;
  const trackUserEvent = useTrackUserEvent();
  const theme = useTheme();
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [collapsedSections, setCollapsedSections] = useState(t1);
  const [filter, setFilter] = useState("all");
  const [editingFieldId, setEditingFieldId] = useState();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [expandedItems, setExpandedItems] = useState(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [filteredExpandedItems, setFilteredExpandedItems] = useState(t3);
  const setExpansion = filter === "all" ? setExpandedItems : setFilteredExpandedItems;
  const t4 = filter === "all" ? expandedItems : filteredExpandedItems;
  let t5;
  if ($[3] !== setExpansion) {
    t5 = (key, expanded) => setExpansion(current => ({
      ...current,
      [key]: expanded
    }));
    $[3] = setExpansion;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t4 || $[6] !== t5) {
    t6 = {
      items: t4,
      setItem: t5
    };
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const expansion = t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = (fieldId, isEditing) => {
      setEditingFieldId(current_0 => {
        if (isEditing) {
          return fieldId;
        }
        return current_0 === fieldId ? undefined : current_0;
      });
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const handleEditingChange = t7;
  let t8;
  if ($[9] !== focusedFieldId || $[10] !== onCloseField || $[11] !== onFocusField || $[12] !== onSelectEvidence) {
    t8 = {
      focusedFieldId,
      onFocusField,
      onCloseField,
      onSelectEvidence,
      onEditingChange: handleEditingChange
    };
    $[9] = focusedFieldId;
    $[10] = onCloseField;
    $[11] = onFocusField;
    $[12] = onSelectEvidence;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const interactions = useAnswerReviewInteractions(t8);
  let t9;
  if ($[14] !== trackUserEvent) {
    t9 = (button, sectionId, isExpanded) => {
      const sectionElement = button.closest("section");
      const scrollContainer = button.closest("main");
      if (isExpanded && sectionElement && scrollContainer) {
        const offset = sectionElement.getBoundingClientRect().top - scrollContainer.getBoundingClientRect().top;
        if (offset < 0) {
          scrollContainer.scrollTop = scrollContainer.scrollTop + offset;
        }
      }
      setCollapsedSections(current_1 => ({
        ...current_1,
        [sectionId]: isExpanded
      }));
      trackUserEvent(isExpanded ? "form review_section_collapsed" : "form review_section_expanded", {
        sectionId
      });
    };
    $[14] = trackUserEvent;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const handleSectionToggle = t9;
  let t10;
  if ($[16] !== review) {
    t10 = getAnswerReviewSummary(review);
    $[16] = review;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const summary = t10;
  let t11;
  if ($[18] !== editingFieldId || $[19] !== filter || $[20] !== focusedFieldId || $[21] !== review.sections) {
    t11 = filterAnswerReviewSections(review.sections, filter, [editingFieldId, focusedFieldId]);
    $[18] = editingFieldId;
    $[19] = filter;
    $[20] = focusedFieldId;
    $[21] = review.sections;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const filteredSections = t11;
  const readOnly = review.mode !== "editable" || !!isDonePending;
  let t12;
  let t13;
  let t14;
  if ($[23] !== review.sections || $[24] !== summary.high || $[25] !== summary.total) {
    const fields = review.sections.flatMap(_temp);
    t12 = summary.total;
    t13 = summary.high;
    t14 = fields.filter(_temp2);
    $[23] = review.sections;
    $[24] = summary.high;
    $[25] = summary.total;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
  } else {
    t12 = $[26];
    t13 = $[27];
    t14 = $[28];
  }
  let t15;
  if ($[29] !== summary.edited || $[30] !== t12 || $[31] !== t13 || $[32] !== t14.length) {
    t15 = {
      all: t12,
      high: t13,
      needsReview: t14.length,
      edited: summary.edited
    };
    $[29] = summary.edited;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14.length;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const filterCounts = t15;
  let t16;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = Object.keys(FILTER_LABELS);
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== filterCounts) {
    t17 = (t16 as AnswerReviewFilter[]).map(filterValue => ({
      value: filterValue,
      display: `${FILTER_LABELS[filterValue]} (${filterCounts[filterValue]})`,
      buttonProps: {
        startIcon: FILTER_COLORS[filterValue] ? () => <ColorBox color={FILTER_COLORS[filterValue]} /> : undefined
      }
    }));
    $[35] = filterCounts;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  const filterOptions = t17;
  let t18;
  if ($[37] !== summary.high) {
    t18 = {
      key: "high",
      count: summary.high,
      color: COLORS.TEAL[300]
    };
    $[37] = summary.high;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== summary.medium) {
    t19 = {
      key: "medium",
      count: summary.medium,
      color: COLORS.FUNCTIONAL.WARNING[500]
    };
    $[39] = summary.medium;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== summary.edited) {
    t20 = {
      key: "edited",
      count: summary.edited,
      color: COLORS.GRAY[600]
    };
    $[41] = summary.edited;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t18 || $[44] !== t19 || $[45] !== t20) {
    t21 = [t18, t19, t20];
    $[43] = t18;
    $[44] = t19;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  const progressSegments = t21;
  let t22;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      containerType: "inline-size"
    };
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  const t23 = interactions.onPointerDownCapture;
  let t24;
  if ($[48] !== focusedFieldId || $[49] !== interactions) {
    t24 = event => {
      if (focusedFieldId && event.target instanceof Element && event.currentTarget.contains(event.target) && !event.target.closest("article, [role=\"listbox\"], [role=\"menu\"]")) {
        interactions.onCloseField();
      }
    };
    $[48] = focusedFieldId;
    $[49] = interactions;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== review.extractionError) {
    t25 = review.extractionError && <Stack role="alert" aria-live="polite" direction="row" alignItems="center" gap={1} px={1.5} py={1} borderRadius={1} bgcolor={COLORS.FUNCTIONAL.ERROR[50]}><AlertTriangleIcon size={16} /><Typography variant="body-xs">{review.extractionError}</Typography></Stack>;
    $[51] = review.extractionError;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Typography variant="eyebrow-xs">Form questions</Typography>;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== summary.answered || $[55] !== summary.total) {
    t27 = <Box>{t26}<Typography id="answer-review-summary-title" component="h2" variant="title-s">{summary.answered} of {summary.total} answered</Typography></Box>;
    $[54] = summary.answered;
    $[55] = summary.total;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  const t28 = summary.total;
  const t29 = summary.answered;
  let t30;
  if ($[57] !== progressSegments || $[58] !== summary.total) {
    t30 = progressSegments.map(segment => <Box key={segment.key} width={summary.total > 0 ? `${segment.count / summary.total * 100}%` : 0} bgcolor={segment.color} />);
    $[57] = progressSegments;
    $[58] = summary.total;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  let t31;
  if ($[60] !== summary.answered || $[61] !== summary.total || $[62] !== t30) {
    t31 = <Box role="progressbar" aria-label="Questions answered" aria-valuemin={0} aria-valuemax={t28} aria-valuenow={t29} display="flex" order={2} width="100%" height={6} borderRadius={3} overflow="hidden" bgcolor={COLORS.GRAY[200]}>{t30}</Box>;
    $[60] = summary.answered;
    $[61] = summary.total;
    $[62] = t30;
    $[63] = t31;
  } else {
    t31 = $[63];
  }
  let t32;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = {
      "@container (max-width: 800px)": {
        width: "100%",
        "& > *": {
          flex: 1
        }
      }
    };
    $[64] = t32;
  } else {
    t32 = $[64];
  }
  let t33;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = {
      flexWrap: "wrap"
    };
    $[65] = t33;
  } else {
    t33 = $[65];
  }
  let t34;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = (_, value) => {
      setFilteredExpandedItems({});
      setFilter(value);
    };
    $[66] = t34;
  } else {
    t34 = $[66];
  }
  let t35;
  if ($[67] !== filter || $[68] !== filterOptions) {
    t35 = <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} ml="auto" sx={t32}><ButtonGroup label="Filter questions" hideLabel={true} sx={t33} value={filter} options={filterOptions} onChange={t34} /></Stack>;
    $[67] = filter;
    $[68] = filterOptions;
    $[69] = t35;
  } else {
    t35 = $[69];
  }
  let t36;
  if ($[70] !== t27 || $[71] !== t31 || $[72] !== t35) {
    t36 = <Stack component="section" aria-labelledby="answer-review-summary-title" gap={1.5} direction="row" flexWrap="wrap" alignItems="center" border={`1px solid ${COLORS.GRAY[200]}`} borderRadius={1} p={2}>{t27}{t31}{t35}</Stack>;
    $[70] = t27;
    $[71] = t31;
    $[72] = t35;
    $[73] = t36;
  } else {
    t36 = $[73];
  }
  let t37;
  if ($[74] !== citationAction || $[75] !== collapsedSections || $[76] !== editingFieldId || $[77] !== expansion || $[78] !== filter || $[79] !== filteredSections || $[80] !== focusedFieldId || $[81] !== handleSectionToggle || $[82] !== interactions.onCloseField || $[83] !== interactions.onEditingChange || $[84] !== interactions.onFocusField || $[85] !== interactions.onSelectEvidence || $[86] !== onAddCitation || $[87] !== onAnswerBlur || $[88] !== onAnswerChange || $[89] !== onCancelCitation || $[90] !== onRecursiveChange || $[91] !== prefersReducedMotion || $[92] !== readOnly || $[93] !== review.sections || $[94] !== selectedEvidence || $[95] !== theme) {
    t37 = filteredSections.length === 0 ? <Stack alignItems="center" py={8} gap={0.5}><Typography component="h2" variant="title-m">No matching questions</Typography><Typography variant="body-s" sx={{
        color: COLORS.TEXT.SECONDARY
      }}>Choose another filter to continue reviewing answers.</Typography></Stack> : filteredSections.map(section_0 => {
      const isExpanded_0 = !collapsedSections[section_0.id];
      const rows = section_0.fields.flatMap(_temp4);
      const showsAnswerHeader = section_0.fields.some(_temp5);
      return <Stack component="section" aria-labelledby={`answer-review-section-${section_0.id}`} key={section_0.id}><Typography id={`answer-review-section-${section_0.id}`} component="h2" variant="title-m" position="sticky" top={0} zIndex={1} bgcolor={COLORS.PRIMITIVES.WHITE} py={0.5} sx={{
          display: "flex",
          minWidth: 0
        }}><ButtonBase aria-expanded={isExpanded_0} aria-controls={`answer-review-section-content-${section_0.id}`} onClick={event_0 => handleSectionToggle(event_0.currentTarget, section_0.id, isExpanded_0)} sx={{
            font: "inherit",
            letterSpacing: "inherit",
            width: "100%",
            justifyContent: "flex-start",
            alignItems: "center",
            textAlign: "left",
            gap: 1,
            borderRadius: 0.5,
            "&.Mui-focusVisible": {
              outline: `2px solid ${COLORS.GRAY[600]}`,
              outlineOffset: 2
            }
          }}><ChevronRightIcon aria-hidden="true" size={20} sx={{
              display: "flex",
              transform: `rotate(${isExpanded_0 ? 90 : 0}deg)`,
              transition: "transform 150ms ease-in-out",
              "@media (prefers-reduced-motion: reduce)": {
                transition: "none"
              }
            }} />{review.sections.findIndex(entry => entry.id === section_0.id) + 1}. {section_0.title}</ButtonBase></Typography><Collapse id={`answer-review-section-content-${section_0.id}`} in={isExpanded_0} timeout={prefersReducedMotion ? 0 : theme.transitions.duration.shorter} inert={!isExpanded_0} aria-hidden={!isExpanded_0}><Box pt={1}>{showsAnswerHeader && <Box display={{
              xs: "none",
              md: "grid"
            }} gridTemplateColumns={{
              xs: "1fr",
              md: "34% 1fr"
            }} bgcolor={COLORS.GRAY[50]}><Typography variant="body-xs" px={1.5} py={1}>Question</Typography><Typography variant="body-xs" px={1.5} py={1}>Answer</Typography></Box>}{rows.map((t38, index) => {
              const {
                field: field_2,
                depth
              } = t38;
              const isSpacedCollection = !!field_2.recursiveNode && (showsAnswerHeader || index > 0);
              const content = field_2.recursiveNode ? <RecursiveAnswerEditor node={field_2.recursiveNode} expansion={expansion} filter={filter} retainedFieldIds={[editingFieldId, focusedFieldId]} readOnly={readOnly} onChange={(node, save) => onRecursiveChange?.(field_2.id, node, save)} onEditingChange={interactions.onEditingChange} onAnswerBlur={onAnswerBlur} onSelectEvidence={interactions.onSelectEvidence} selectedEvidence={selectedEvidence} onFocusField={interactions.onFocusField} onCloseField={interactions.onCloseField} onCancelCitation={onCancelCitation} onAddCitation={onAddCitation} focusedFieldId={focusedFieldId} citationAction={citationAction} /> : <><AnswerReviewFieldRow field={field_2} depth={depth} readOnly={readOnly} onAnswerChange={onAnswerChange} onAnswerBlur={onAnswerBlur} onSelectEvidence={interactions.onSelectEvidence} selectedEvidence={selectedEvidence} onFocusField={interactions.onFocusField} onCloseField={interactions.onCloseField} onCancelCitation={onCancelCitation} onAddCitation={onAddCitation} citationAction={citationAction} isFocused={focusedFieldId === field_2.id} onEditingChange={interactions.onEditingChange} /></>;
              return <Box key={field_2.id} mt={isSpacedCollection ? 1 : undefined}>{content}</Box>;
            })}</Box></Collapse></Stack>;
    });
    $[74] = citationAction;
    $[75] = collapsedSections;
    $[76] = editingFieldId;
    $[77] = expansion;
    $[78] = filter;
    $[79] = filteredSections;
    $[80] = focusedFieldId;
    $[81] = handleSectionToggle;
    $[82] = interactions.onCloseField;
    $[83] = interactions.onEditingChange;
    $[84] = interactions.onFocusField;
    $[85] = interactions.onSelectEvidence;
    $[86] = onAddCitation;
    $[87] = onAnswerBlur;
    $[88] = onAnswerChange;
    $[89] = onCancelCitation;
    $[90] = onRecursiveChange;
    $[91] = prefersReducedMotion;
    $[92] = readOnly;
    $[93] = review.sections;
    $[94] = selectedEvidence;
    $[95] = theme;
    $[96] = t37;
  } else {
    t37 = $[96];
  }
  let t38;
  if ($[97] !== t25 || $[98] !== t36 || $[99] !== t37) {
    t38 = <Stack p={2} gap={2}>{t25}{t36}{t37}</Stack>;
    $[97] = t25;
    $[98] = t36;
    $[99] = t37;
    $[100] = t38;
  } else {
    t38 = $[100];
  }
  let t39;
  if ($[101] !== interactions.onPointerDownCapture || $[102] !== t24 || $[103] !== t38) {
    t39 = <Box component="main" aria-label="Answer review" height="100%" overflow="auto" bgcolor={COLORS.PRIMITIVES.WHITE} sx={t22} onPointerDownCapture={t23} onClick={t24}>{t38}</Box>;
    $[101] = interactions.onPointerDownCapture;
    $[102] = t24;
    $[103] = t38;
    $[104] = t39;
  } else {
    t39 = $[104];
  }
  return t39;
}
function _temp5(field_1) {
  return !field_1.recursiveNode;
}
function _temp4(field_0) {
  return [{
    field: field_0,
    depth: 0
  }, ...(field_0.children ?? []).map(_temp3)];
}
function _temp3(child) {
  return {
    field: child,
    depth: 1
  };
}
function _temp2(field) {
  return fieldMatchesFilter(field, "needsReview");
}
function _temp(section) {
  return getActiveFields(section.fields);
}